/** Portable Audience Set wire schemas. Runtime compilation stays in Web. */
import * as z from 'zod';
import { audienceSetProfileOverlayFieldSchema, audienceSetProfileOverlaySchema, } from './audience-set-profile-overlay.js';
export const audienceSetFilterSchema = z
    .object({
    age_range: z
        .tuple([z.number(), z.number()])
        .or(z.object({
        min: z.number(),
        max: z.number(),
    }))
        .optional(),
    gender: z.enum(['Male', 'Female', 'All']).optional(),
    states: z.array(z.string().trim().min(1)).optional(),
    income_bands: z.array(z.string().trim().min(1)).optional(),
    education: z.array(z.string().trim().min(1)).optional(),
    employment_statuses: z.array(z.string().trim().min(1)).optional(),
    occupations: z.array(z.string().trim().min(1)).optional(),
    industries: z.array(z.string().trim().min(1)).optional(),
    metro_rest_classes: z.array(z.string().trim().min(1)).optional(),
})
    .strict();
export const audienceSetQuotaDistributionSchema = z.record(z.string(), z.number().min(0));
export const audienceSetQuotaTargetsSchema = z
    .object({
    age: audienceSetQuotaDistributionSchema.optional(),
    gender: audienceSetQuotaDistributionSchema.optional(),
    state: audienceSetQuotaDistributionSchema.optional(),
    dominantGccsaName: audienceSetQuotaDistributionSchema.optional(),
    metroRestClass: audienceSetQuotaDistributionSchema.optional(),
})
    .strict();
export const audienceSetSampleMixSchema = z
    .object({
    mode: z.enum(['random', 'quota']).default('random'),
    targets: audienceSetQuotaTargetsSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.mode === 'random') {
        if (value.targets && Object.keys(value.targets).length > 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'random sampling cannot include quota targets',
                path: ['targets'],
            });
        }
        return;
    }
    if (!value.targets || Object.keys(value.targets).length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'quota sampling requires at least one quota target',
            path: ['targets'],
        });
        return;
    }
    for (const [field, distribution] of Object.entries(value.targets)) {
        if (!distribution || Object.keys(distribution).length === 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `quota target distribution for ${field} must include at least one positive finite value`,
                path: ['targets', field],
            });
            continue;
        }
        const hasPositiveFiniteValue = Object.values(distribution).some((target) => Number.isFinite(target) && target > 0);
        if (!hasPositiveFiniteValue) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: `quota target distribution for ${field} must include at least one positive finite value`,
                path: ['targets', field],
            });
        }
    }
});
export { audienceSetProfileOverlayFieldSchema, audienceSetProfileOverlaySchema };
export const audienceSetMetadataSchema = z
    .object({
    family: z.string().trim().min(1).optional(),
    source: z.string().trim().min(1).optional(),
    version: z.string().trim().min(1).optional(),
    market_country: z.string().trim().min(1).optional(),
    base_profile_label: z.literal('Base Profile').optional(),
    shopper_background_label: z.literal('Shopper Background').optional(),
    product_context_label: z.literal('Product Being Tested').optional(),
})
    .strict();
export const audienceSetAnalyticsScopeSchema = z
    .object({
    type: z.enum(['posthog', 'amplitude', 'combined_first_party_analytics', 'analytics_read_model']),
})
    .strict();
export const audienceSetSourceScopeSchema = z
    .object({
    type: z.enum(['manual', 'shopify', 'imported', 'posthog', 'combined_first_party_analytics']).default('manual'),
    shop: z.string().trim().min(1).optional(),
    connection_id: z.string().trim().min(1).optional(),
    source_updated_at: z.string().trim().min(1).optional(),
    analytics_scope: audienceSetAnalyticsScopeSchema.optional(),
})
    .strict();
export const audienceSetSchema = z
    .object({
    schema_version: z.literal('audience-set.v1').default('audience-set.v1'),
    id: z.string().trim().min(1),
    version: z.number().int().positive().default(1),
    name: z.string().trim().min(1),
    population_id: z.string().trim().min(1),
    shopify_shop_domain: z.string().trim().min(1).optional(),
    audience_filters: audienceSetFilterSchema.optional(),
    sample_mix: audienceSetSampleMixSchema.optional(),
    profile_overlay: audienceSetProfileOverlaySchema.optional(),
    source_scope: audienceSetSourceScopeSchema.default({ type: 'manual' }),
    metadata: audienceSetMetadataSchema.optional(),
    created_at: z.string().trim().min(1).optional(),
    updated_at: z.string().trim().min(1).optional(),
})
    .strict();
//# sourceMappingURL=audience-set.js.map